// Payments Module Tour
// Invoices, payment links, subscriptions, products and the money side of your business
// Selector policy: step 1 pins to the LIVE-VERIFIED sidebar id (#sb_payments); the teaching steps are
// deliberate centered cards — no guessed element selectors (a wrong spotlight is worse than none).

export const paymentsTour = {
  id: 'payments',
  name: 'Payments Module Tour',
  displayName: 'Get Paid With {{brandName}}',
  description: 'Invoices, payment links, subscriptions, products and the money side of your business',
  estimatedDuration: '5 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],

  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },

  steps: [
    {
      element: '#sb_payments',
      popover: {
        title: "💳 Welcome to Payments",
        description: `
          This is where the money moves. Invoices, subscriptions, payment links, products — every dollar your business collects runs through here. Let us walk the whole picture.
        `,
        side: 'right',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🔌 Connect a Gateway First",
        description: `
          Nothing charges until a **payment gateway** is connected. Link **Stripe** or **PayPal** once under Integrations, and every invoice, funnel, and payment link in your account can take money instantly.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🧾 Invoices & Estimates",
        description: `
          Send a one-off **invoice**, or an **estimate** the client can approve and convert. Add line items, taxes, and due dates — clients pay online with a card, and the record updates itself.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🔁 Recurring Invoices & Subscriptions",
        description: `
          Bill the same client on a schedule. **Recurring invoices** send themselves; **subscriptions** charge automatically. Both show status at a glance — active, paused, or failed — so churn never hides.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🔗 Payment Links",
        description: `
          The fastest way to take money: create a **payment link**, share it anywhere — text, email, social, a QR code — and get paid without building a page.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "📦 Products & Collections",
        description: `
          Everything you sell lives in your **product catalog**: one-time or recurring, with prices, images, and variants. Group them into **collections** to sell them together.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🏷️ Taxes & Coupons",
        description: `
          Define **tax rates** once and apply them to any product or invoice. Create **coupons** and **gift cards** to run promotions without discounting by hand.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "📊 Orders & Transactions",
        description: `
          **Orders** shows what sold. **Transactions** shows every charge, refund, and failure. Together they answer "did that payment land?" in one click.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🛒 Abandoned Checkouts",
        description: `
          Someone started paying and stopped. This list captures them — pair it with an automation and you recover revenue that would have quietly disappeared.
        `,
        side: 'over',
        align: 'center'
      }
    },

    {
      popover: {
        title: "🎉 You Run the Money",
        description: `
          You now know the payments stack:

✅ Gateway → invoices → subscriptions
✅ Payment links for instant sales
✅ Products, taxes, coupons
✅ Orders, transactions, recovery

**Next:** connect your gateway, add one product, and send a test payment link to yourself.
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Done →'
      }
    }
  ]
};
